// Interview State Management in sessionStorage

import { getCurrentInterviewId, removeThankYouMessage } from './storageHelpers'
import { clearTopicState } from './topicStateStorage'

export interface ChatMessage {
  id: string
  type: 'question' | 'answer' | 'feedback' | 'clarification'
  content: string
  timestamp: string
  ttsAudioData?: string
  audioUrl?: string
  audioDuration?: number
  questionIndex: number
  questionId?: string  // Question UUID for video selection
  jobId?: string       // Question set id when using catalog questions; persisted on Answer via mark_question_as_shown
  questionType?: string
  payload?: Record<string, unknown>
  ttsCacheKey?: string
  recordingCacheKey?: string
  transcription?: string
  isClarifyingQuestion?: boolean
  isAutoSubmit?: boolean // Flag to hide test cases for auto-submit cases
}

export interface QuestionAnalysis {
  question: string
  answer: string
  score: number
  short_feedback: string
  detailed_feedback: string
}

export interface DynamicQuestion {
  id: string
  question_text: string
  theme: string
  is_start_question: boolean
  question_type: string
  payload: Record<string, unknown>
}

export interface FeedbackData {
  score: number
  feedback: string
  short_feedback?: string
  best_answer: string
  time_taken: string
  questions_left: number
  tts_audio_data?: string
  detailed_feedback?: string
  test_results?: Record<string, boolean>
  is_precheck?: boolean
}

/** System design discussion message (clarification Q&A) — persisted in interview state like JD/resume. */
export type SystemDesignDiscussionMessage = { role: string; content: string; msgType?: string }

export interface InterviewState {
  currentQuestionIndex: number
  chatMessages: ChatMessage[]
  answerTimes: Record<number, number>
  clarificationAttempts: Record<string, unknown>
  questionAnalyses: QuestionAnalysis[]
  dynamicQuestions: DynamicQuestion[]
  dynamicTtsData: unknown[]
  lastAnswerScore: number | null
  feedbackData: Record<number, FeedbackData>
  recordings: Record<string, unknown>
  timerExpired: boolean
  interviewSubmitted: boolean
  feedbackCompleted: boolean
  startTime: number
  interviewId: number | string
  attemptNumber: number
  status: 'in-progress' | 'completed' | 'abandoned'
  /** System design: discussion tab messages per question id (key = dag question uuid). Restored with state on refresh. */
  systemDesignDiscussions?: Record<string, SystemDesignDiscussionMessage[]>
}

const STORAGE_KEY_PREFIX = 'interview_state_'

/**
 * Generate storage key for an interview
 */
export function getInterviewStorageKey(interviewId: number | string, attemptNumber: number): string {
  return `${STORAGE_KEY_PREFIX}${interviewId}_${attemptNumber}`
}

/**
 * Initialize interview state from start API response
 */
export function initializeInterviewState(
  startApiResponse: {
    question: {
      id: string
      question_text: string
      theme: string
      is_start_question: boolean
      question_type: string
      payload: Record<string, unknown>
      tts_audio_data?: string
      job_id?: string | null  // Set when question is from graph (USE_GRAPH_FOR_QUESTIONS)
    }
    is_start_question: boolean
    duration_minutes: number
    stop_threshold_minutes: number
  },
  interviewId: number | string,
  attemptNumber: number
): InterviewState {
  const now = new Date().toISOString()

  // Extract question data from the response
  const questionData = startApiResponse.question

  // Create the first question message
  const firstQuestion: ChatMessage = {
    id: 'question-0',
    type: 'question',
    content: questionData.question_text,
    timestamp: now,
    ttsAudioData: questionData.tts_audio_data || '', // Store TTS audio data if available
    questionIndex: 0,
    questionId: questionData.id,
    jobId: questionData.job_id ?? undefined,  // Only set when question is from graph
    questionType: questionData.question_type,
    payload: questionData.payload
  }

  // Create dynamic question object
  const dynamicQuestion: DynamicQuestion = {
    id: questionData.id,
    question_text: questionData.question_text,
    theme: questionData.theme,
    is_start_question: startApiResponse.is_start_question,
    question_type: questionData.question_type,
    payload: questionData.payload
  }

  const initialState: InterviewState = {
    currentQuestionIndex: 0,
    chatMessages: [firstQuestion],
    answerTimes: {},
    clarificationAttempts: {},
    questionAnalyses: [],
    dynamicQuestions: [dynamicQuestion],
    dynamicTtsData: [],
    lastAnswerScore: null,
    feedbackData: {},
    recordings: {},
    timerExpired: false,
    interviewSubmitted: false,
    feedbackCompleted: false,
    startTime: Date.now(),
    interviewId,
    attemptNumber,
    status: 'in-progress',
    systemDesignDiscussions: {}
  }

  return initialState
}

/**
 * Initialize practice question state from sessionStorage data
 */
export function initializePracticeQuestionState(
  practiceQuestionData: {
    attempt_id: string
    question_id: string
    question_text: string
    requires_resume: boolean
    tts_audio_data?: string | null
  },
  interviewId: string,
  attemptNumber: number
): InterviewState {
  const now = new Date().toISOString()

  const firstQuestion: ChatMessage = {
    id: 'question-0',
    type: 'question',
    content: practiceQuestionData.question_text,
    timestamp: now,
    ttsAudioData: practiceQuestionData.tts_audio_data || '',
    questionIndex: 0,
    questionType: 'text',
    payload: {}
  }

  const dynamicQuestion: DynamicQuestion = {
    id: practiceQuestionData.question_id,
    question_text: practiceQuestionData.question_text,
    theme: 'behavioral',
    is_start_question: true,
    question_type: 'text',
    payload: {}
  }

  const initialState: InterviewState = {
    currentQuestionIndex: 0,
    chatMessages: [firstQuestion],
    answerTimes: {},
    clarificationAttempts: {},
    questionAnalyses: [],
    dynamicQuestions: [dynamicQuestion],
    dynamicTtsData: [],
    lastAnswerScore: null,
    feedbackData: {},
    recordings: {},
    timerExpired: false,
    interviewSubmitted: false,
    feedbackCompleted: false,
    startTime: Date.now(),
    interviewId,
    attemptNumber,
    status: 'in-progress',
    systemDesignDiscussions: {}
  }

  return initialState
}

/**
 * Update system design discussion for a question (persisted with interview state).
 */
export function setSystemDesignDiscussion(
  state: InterviewState,
  questionId: string,
  messages: SystemDesignDiscussionMessage[]
): InterviewState {
  return {
    ...state,
    systemDesignDiscussions: {
      ...(state.systemDesignDiscussions ?? {}),
      [questionId]: messages
    }
  }
}

/**
 * Save interview state to sessionStorage
 * Excludes large audio data to prevent QuotaExceededError
 * Audio data is stored in Cache Storage API and will be restored on page load
 */
export function saveInterviewState(state: InterviewState): void {
  try {
    const key = getInterviewStorageKey(state.interviewId, state.attemptNumber)

    // Create a copy of state without large audio data
    // Keep cache keys so we can restore audio from Cache Storage
    const stateToSave: InterviewState = {
      ...state,
      // Remove ttsAudioData from chat messages to save space, but keep cache keys
      chatMessages: state.chatMessages.map(msg => {
        const { ttsAudioData, ...rest } = msg
        // Keep ttsCacheKey and recordingCacheKey for restoration
        return rest as ChatMessage
      }),
      // Remove tts_audio_data from feedback data to save space
      feedbackData: Object.fromEntries(
        Object.entries(state.feedbackData).map(([key, feedback]) => {
          const { tts_audio_data, ...rest } = feedback
          return [key, rest]
        })
      )
    }

    sessionStorage.setItem(key, JSON.stringify(stateToSave))
  } catch (error) {
    // Error saving interview state

    // If still failing, try to clear old interview states
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      try {
        // Clear all interview state keys except the current one
        const currentKey = getInterviewStorageKey(state.interviewId, state.attemptNumber)
        const allKeys = Object.keys(sessionStorage)
        allKeys.forEach(key => {
          if (key.startsWith(STORAGE_KEY_PREFIX) && key !== currentKey) {
            sessionStorage.removeItem(key)
          }
        })

        // Try saving again after cleanup
        const stateToSave: InterviewState = {
          ...state,
          chatMessages: state.chatMessages.map(msg => {
            const { ttsAudioData, ...rest } = msg
            return rest as ChatMessage
          }),
          feedbackData: Object.fromEntries(
            Object.entries(state.feedbackData).map(([key, feedback]) => {
              const { tts_audio_data, ...rest } = feedback
              return [key, rest]
            })
          )
        }
        sessionStorage.setItem(currentKey, JSON.stringify(stateToSave))
      } catch (retryError) {
        // Failed to save even after cleanup
      }
    }
  }
}

/**
 * True when the user already called /interview/start and can resume after a full page reload.
 * Does not rely on __interview_unloading (refresh-only beacon).
 */
export function hasResumableInterviewSession(): boolean {
  if (typeof window === 'undefined') return false

  const interviewId = getCurrentInterviewId()
  if (!interviewId) return false

  let current: {
    startInterviewData?: { question?: unknown }
    firstQuestion?: unknown
    attemptNumber?: unknown
  } | null = null
  const currentStr = sessionStorage.getItem('currentInterview')
  if (currentStr) {
    try {
      current = JSON.parse(currentStr)
    } catch {
      current = null
    }
  }

  // `currentAttemptNumber` is the normal source, but the meet page also records the attempt
  // inside the currentInterview blob. Falling back to it keeps the JD flow, which does not
  // always write the separate key before a reload, from looking like a brand-new session.
  const attemptStr =
    sessionStorage.getItem('currentAttemptNumber') ??
    (current?.attemptNumber != null ? String(current.attemptNumber) : null)
  if (!attemptStr) return false

  const attemptNumber = parseInt(attemptStr, 10)
  if (Number.isNaN(attemptNumber)) return false

  // Only treat as resumable if the interview was actually started. A bare
  // { interview_type: 'jd_based' } written by applyServerInterviewType during page init should
  // NOT trigger auto-resume -- the candidate still needs to click Start Interview.
  //
  // `startInterviewData.question` depends on the shape of the start response and the JD flow
  // does not reliably carry it, which is why a JD refresh replayed the start screen while a mock
  // one resumed. `firstQuestion` is a plain string the meet page writes after every successful
  // start, so it is the dependable "this interview began" marker.
  if (current?.startInterviewData?.question) return true
  if (typeof current?.firstQuestion === 'string' && current.firstQuestion.trim() !== '') {
    return true
  }

  return loadInterviewState(interviewId, attemptNumber) !== null
}

const INTRO_DELIVERED_KEY_PREFIX = 'interview_intro_delivered_'

/** Per interview/attempt key for "the welcome/candidate intro has been delivered". */
export function getInterviewIntroDeliveredKey(
  interviewId: number | string,
  attemptNumber: number
): string {
  return `${INTRO_DELIVERED_KEY_PREFIX}${interviewId}_${attemptNumber}`
}

/**
 * Persist that the intro sequence (interviewer intro + candidate intro prompt) finished for
 * this attempt. A refresh can then resume at the current question instead of replaying the
 * welcome; a fresh start (no marker) still plays it.
 */
export function markInterviewIntroDelivered(
  interviewId: number | string | null | undefined,
  attemptNumber: number | null | undefined
): void {
  if (typeof window === 'undefined' || !interviewId || !attemptNumber) return
  try {
    sessionStorage.setItem(
      getInterviewIntroDeliveredKey(interviewId, attemptNumber),
      '1'
    )
  } catch {
    /* quota / private mode */
  }
}

export function hasInterviewIntroDelivered(
  interviewId: number | string | null | undefined,
  attemptNumber: number | null | undefined
): boolean {
  if (typeof window === 'undefined' || !interviewId || !attemptNumber) return false
  try {
    return (
      sessionStorage.getItem(
        getInterviewIntroDeliveredKey(interviewId, attemptNumber)
      ) === '1'
    )
  } catch {
    return false
  }
}

/**
 * Load interview state from sessionStorage
 */
export function loadInterviewState(interviewId: number | string, attemptNumber: number): InterviewState | null {
  try {
    const key = getInterviewStorageKey(interviewId, attemptNumber)
    const stored = sessionStorage.getItem(key)

    if (!stored) {
      return null
    }

    return JSON.parse(stored) as InterviewState
  } catch (error) {
    // Error loading interview state
    return null
  }
}

/**
 * Clear interview state from sessionStorage
 */
export function clearInterviewState(interviewId: number | string, attemptNumber: number): void {
  try {
    const key = getInterviewStorageKey(interviewId, attemptNumber)
    sessionStorage.removeItem(key)
    // A fresh start for this attempt must play the intro again.
    sessionStorage.removeItem(getInterviewIntroDeliveredKey(interviewId, attemptNumber))

    // Also clear topic state for this interview/attempt
    try {
      clearTopicState(interviewId, attemptNumber)
    } catch (error) {
      // Don't throw - continue with other cleanup
    }
  } catch (error) {
    // Error clearing interview state
  }
}

/**
 * Clear all interview-related data from sessionStorage and IndexedDB
 */
export async function clearAllInterviewData(): Promise<void> {
  try {
    // Get interview ID and attempt number BEFORE clearing
    // Use helper function to get UUID string from currentInterview object
    const currentInterviewId = getCurrentInterviewId()
    const currentAttemptNumber = sessionStorage.getItem('currentAttemptNumber')

    // Clear interview state from sessionStorage
    // Note: clearInterviewState now also clears topic state
    if (currentInterviewId && currentAttemptNumber) {
      clearInterviewState(currentInterviewId, parseInt(currentAttemptNumber))
    }

    // Clear all interview_state_* / intro-delivered keys
    const allKeys = Object.keys(sessionStorage)
    allKeys.forEach(key => {
      if (key.startsWith(STORAGE_KEY_PREFIX)) {
        sessionStorage.removeItem(key)
      }
      if (key.startsWith(INTRO_DELIVERED_KEY_PREFIX)) {
        sessionStorage.removeItem(key)
      }
      // Also clear topic_state_* keys
      if (key.startsWith('topic_state_')) {
        sessionStorage.removeItem(key)
      }
    })

    // Clear thank-you message (scoped + legacy)
    removeThankYouMessage(currentInterviewId ?? undefined, currentAttemptNumber ? parseInt(currentAttemptNumber) : undefined)

    // Clear specific sessionStorage items
    const keysToRemove = [
      'currentAttemptNumber',
      'currentInterview',
      'interviewRole',
      'interviewId',
    ]

    keysToRemove.forEach(key => {
      sessionStorage.removeItem(key)
    })

    // The autoplay flag is written to sessionStorage (see JDVideoInterviewSession), so
    // clearing only localStorage left a stale flag that silently suppressed the first
    // question's audio for the rest of the tab's life. Clear both, collecting the keys
    // first because removing while iterating skips entries.
    if (typeof window !== 'undefined') {
      for (const storage of [window.sessionStorage, window.localStorage]) {
        const stale: string[] = []
        for (let i = 0; i < storage.length; i++) {
          const key = storage.key(i)
          if (key?.startsWith('firstQuestionAutoPlayed_')) stale.push(key)
        }
        stale.forEach((key) => storage.removeItem(key))
      }
    }

    // Clear audio recordings from Cache Storage
    if (currentInterviewId && currentAttemptNumber) {
      try {
        const { clearInterviewAudioCache } = await import('./audioCache')
        await clearInterviewAudioCache(
          currentInterviewId,
          parseInt(currentAttemptNumber)
        )
      } catch (audioError) {
        // Don't throw - continue with other cleanup
      }
    }

    // Also clear old IndexedDB audio cache if it exists (for backward compatibility)
    try {
      const dbRequest = indexedDB.deleteDatabase('audio-cache')
      await new Promise((resolve, reject) => {
        dbRequest.onsuccess = () => {
          resolve(undefined)
        }
        dbRequest.onerror = () => {
          reject(dbRequest.error)
        }
        dbRequest.onblocked = () => {
          resolve(undefined)
        }
      })
    } catch (dbError) {
      // Don't throw - continue with other cleanup
    }
  } catch (error) {
    throw error
  }
}

/** Session keys used by the system design interview page (orchestrator + canvas restore). */
function clearSystemDesignSessionStorageKeys(): void {
  if (typeof window === 'undefined') return
  const keys = Object.keys(sessionStorage)
  for (const key of keys) {
    if (
      key.startsWith('sd_canvas_') ||
      key.startsWith('sd_ui_') ||
      key === 'sdInterviewId' ||
      key === 'sdOrchestratorSessionId' ||
      key === 'sdProblemConfigKey' ||
      key.startsWith('sdOrchestratorSessionId_')
    ) {
      sessionStorage.removeItem(key)
    }
  }
}

/**
 * Persist analysis for `/results`, embed interview/attempt ids for analytics and navigation
 * (they are removed when clearing the in-progress session), then clear interview session data.
 * Does not remove `interviewAnalysis` — `clearAllInterviewData` leaves that key intact.
 */
export async function persistCompletedInterviewAnalysis(
  analysisData: Record<string, unknown>,
  interviewId: number | string | null | undefined,
  attemptNumber: number | null | undefined
): Promise<void> {
  const enriched: Record<string, unknown> = { ...analysisData }
  if (interviewId != null && interviewId !== '') {
    enriched.completion_interview_id = String(interviewId)
  }
  if (attemptNumber != null && typeof attemptNumber === 'number' && !Number.isNaN(attemptNumber)) {
    enriched.completion_attempt_number = attemptNumber
  }
  sessionStorage.setItem('interviewAnalysis', JSON.stringify(enriched))
  try {
    await clearAllInterviewData()
  } catch (e) {
    console.error('Failed to clear interview session after completion:', e)
  }
  try {
    clearSystemDesignSessionStorageKeys()
  } catch (e) {
    console.error('Failed to clear system design session keys after completion:', e)
  }
}

/**
 * Add an answer message to the interview state
 */
export function addAnswerMessage(
  state: InterviewState,
  audioUrl: string,
  audioDuration: number,
  questionIndex: number,
  transcription?: string
): InterviewState {
  const answerId = `answer-${questionIndex}-${Date.now()}`
  const recordingCacheKey = `recording-${state.interviewId}-${state.attemptNumber}-${questionIndex}`

  const answerMessage: ChatMessage = {
    id: answerId,
    type: 'answer',
    content: transcription || '',
    timestamp: new Date().toISOString(),
    audioUrl,
    audioDuration,
    questionIndex,
    recordingCacheKey,
    transcription
  }

  return {
    ...state,
    chatMessages: [...state.chatMessages, answerMessage]
  }
}

/**
 * Add a clarification message to the interview state
 */
export function addClarificationMessage(
  state: InterviewState,
  clarificationText: string,
  questionIndex: number,
  ttsAudioData?: string,
  questionType?: string,
  payload?: Record<string, unknown>
): InterviewState {
  const clarificationId = `clarification-${questionIndex}-${Date.now()}`

  const clarificationMessage: ChatMessage = {
    id: clarificationId,
    type: 'clarification',
    content: clarificationText,
    timestamp: new Date().toISOString(),
    questionIndex,
    isClarifyingQuestion: true,
    ttsAudioData: ttsAudioData || '',
    questionType: questionType || 'text',
    payload: payload || {}
  }

  return {
    ...state,
    chatMessages: [...state.chatMessages, clarificationMessage]
  }
}

/**
 * Add a feedback message to the interview state
 */
export function addFeedbackMessage(
  state: InterviewState,
  feedback: string,
  questionIndex: number,
  score: number,
  detailedFeedback: string,
  timeTaken: number,
  ttsAudioData?: string,
  testResults?: Record<string, boolean>,
  isAutoSubmit?: boolean,
  shortFeedback?: string,
  isPrecheck?: boolean
): InterviewState {
  const feedbackId = `generating-${questionIndex}-${Date.now()}`
  const ttsCacheKey = `feedback-tts-${state.interviewId}-${state.attemptNumber}-${feedbackId}`

  const feedbackMessage: ChatMessage = {
    id: feedbackId,
    type: 'feedback',
    content: feedback,
    timestamp: new Date().toISOString(),
    questionIndex,
    ttsCacheKey,
    isAutoSubmit
  }

  // Add to question analyses
  const analysis: QuestionAnalysis = {
    question: state.dynamicQuestions[questionIndex]?.question_text || '',
    answer: '',
    score,
    short_feedback: feedback,
    detailed_feedback: detailedFeedback
  }

  // Format time taken
  const minutes = Math.floor(timeTaken / 60)
  const seconds = timeTaken % 60
  const formattedTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`

  // Add to feedback data
  const feedbackDataEntry: FeedbackData = {
    score,
    feedback,
    short_feedback: shortFeedback,
    best_answer: detailedFeedback,
    time_taken: formattedTime,
    questions_left: state.dynamicQuestions.length - questionIndex - 1,
    tts_audio_data: ttsAudioData,
    test_results: testResults,
    ...(isPrecheck ? { is_precheck: true } : {}),
  }

  return {
    ...state,
    chatMessages: [...state.chatMessages, feedbackMessage],
    questionAnalyses: [...state.questionAnalyses, analysis],
    feedbackData: {
      ...state.feedbackData,
      [questionIndex]: feedbackDataEntry
    },
    answerTimes: {
      ...state.answerTimes,
      [questionIndex]: timeTaken
    },
    lastAnswerScore: score
  }
}

/**
 * Add a new question to the interview state
 */
export function addQuestionMessage(
  state: InterviewState,
  question: string,
  ttsAudioData: string,
  questionType: string,
  payload: Record<string, unknown>,
  questionId?: string,
  jobId?: string
): InterviewState {
  const nextIndex = state.currentQuestionIndex + 1

  const questionMessage: ChatMessage = {
    id: `question-${nextIndex}`,
    type: 'question',
    content: question,
    timestamp: new Date().toISOString(),
    ttsAudioData,
    questionIndex: nextIndex,
    questionId,
    jobId,  // Set when next question is from graph (API returned job_id)
    questionType,
    payload
  }

  const dynamicQuestion: DynamicQuestion = {
    id: questionId || `question-${nextIndex}`,
    question_text: question,
    theme: '',
    is_start_question: false,
    question_type: questionType,
    payload
  }

  return {
    ...state,
    currentQuestionIndex: nextIndex,
    chatMessages: [...state.chatMessages, questionMessage],
    dynamicQuestions: [...state.dynamicQuestions, dynamicQuestion]
  }
}

/**
 * Update interview status
 */
export function updateInterviewStatus(
  state: InterviewState,
  status: InterviewState['status']
): InterviewState {
  return {
    ...state,
    status
  }
}

/**
 * Update clarification attempts in interview state
 */
export function updateClarificationAttempts(
  state: InterviewState,
  questionIndex: number,
  attempts: number
): InterviewState {
  return {
    ...state,
    clarificationAttempts: {
      ...state.clarificationAttempts,
      [questionIndex]: attempts
    }
  }
}
